import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError } from "../lib/api";
import { useChangePassword } from "./useAuth";

type Props = {
  appName: string;
  redirectAfter?: string;
};

/**
 * Zwangs-Passwort-Wechsel bei erstem Login + freiwilliger Wechsel über Profil.
 */
export function ChangePasswordCard({ appName, redirectAfter = "/" }: Props) {
  const navigate = useNavigate();
  const change = useChangePassword(appName);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const tooShort = next.length > 0 && next.length < 12;
  const mismatch = confirm.length > 0 && next !== confirm;
  const score = passwordScore(next);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (next !== confirm || next.length < 12) return;
    setErrorMsg("");
    try {
      await change.mutateAsync({ current_password: current, new_password: next });
      navigate(redirectAfter, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setErrorMsg("Aktuelles Passwort falsch.");
      else if (err instanceof ApiError && err.status === 400) setErrorMsg("Neues Passwort muss sich vom alten unterscheiden.");
      else setErrorMsg("Konnte nicht geändert werden.");
    }
  }

  return (
    <div className="m-app login-shell">
      <div className="login-card">
        <div className="login-logo">Passwort ändern</div>
        <div className="login-sub">Mindestens 12 Zeichen, gerne länger.</div>
        <form onSubmit={submit} className="login-form">
          <label className="login-label">
            <span>Aktuelles Passwort</span>
            <input type="password" autoComplete="current-password"
              value={current} onChange={(e) => setCurrent(e.target.value)} required autoFocus />
          </label>
          <label className="login-label">
            <span>Neues Passwort</span>
            <input type="password" autoComplete="new-password" minLength={12}
              value={next} onChange={(e) => setNext(e.target.value)} required />
          </label>
          {next.length > 0 ? (
            <div className="pw-meter">
              <div className={`pw-meter-fill score-${score}`} />
              <span className="pw-meter-label">
                {tooShort ? "zu kurz (min 12)" : ["sehr schwach","schwach","okay","gut","stark"][score]}
              </span>
            </div>
          ) : null}
          <label className="login-label">
            <span>Wiederholen</span>
            <input type="password" autoComplete="new-password"
              value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
          </label>
          {mismatch ? <div className="login-error">Passwörter stimmen nicht überein.</div> : null}
          <button type="submit" className="login-btn"
            disabled={change.isPending || tooShort || mismatch || !current || !next || !confirm}>
            {change.isPending ? "Speichere…" : "Passwort speichern"}
          </button>
          {errorMsg ? <div className="login-error">{errorMsg}</div> : null}
        </form>
      </div>
    </div>
  );
}

function passwordScore(pw: string): number {
  if (pw.length < 12) return 0;
  let pts = 0;
  if (/[a-z]/.test(pw)) pts++;
  if (/[A-Z]/.test(pw)) pts++;
  if (/\d/.test(pw)) pts++;
  if (/[^a-zA-Z\d]/.test(pw)) pts++;
  if (pw.length >= 16) pts = Math.min(4, pts + 1);
  return Math.min(4, pts);
}
